import { environment } from "@env";

export interface DemoApplicationCard {
	title: string;
	titleImage: string;
	description: string;
	link: string;
	color: string;
	chips: string[];
	repoUrl: DemoRemoteUrls;
	coreId: string;
	authFreeBuildId?: string;
}

export enum DemoRemoteUrls {
	batteryState = "https://github.com/signaloid/Signaloid-Demo-Batteries-StateOfChargeEstimation",
	moonfire = "https://github.com/signaloid/Signaloid-Demo-Finance-MoonfireVentureCapitalPortfolioModeling",
	taxFreeRetirement = "https://github.com/signaloid/Signaloid-Demo-Finance-InvestmentRetirementAccount",
	mlx = "https://github.com/signaloid/Signaloid-Demo-Sensors-MLX90640ConversionRoutines",
	bme = "https://github.com/signaloid/Signaloid-Demo-Sensors-BME680ConversionRoutines",
	brownHam = "https://github.com/signaloid/Signaloid-Demo-Metallurgy-BrownHamModel",
	var = "https://github.com/signaloid/Signaloid-Demo-Finance-OosterleeGrzelakBookGeometricBrownianMotionExample-Internal.git",
}

export interface DemoApplicationRepository {
	RemoteURL: string;
	Commit: string;
	Branch: string;
	BuildDirectory: string;
	Arguments: string;
	Core?: string;
}

export const DemoRepositoriesMap: { [key in DemoRemoteUrls]: DemoApplicationRepository } = {
	[DemoRemoteUrls.batteryState]: {
		RemoteURL: DemoRemoteUrls.batteryState,
		Commit: "HEAD",
		Branch: "main",
		BuildDirectory: "src",
		Arguments: "",
		Core: "cor_7354fce65c415e639bd3bb60f48fd9a5",
	},
	[DemoRemoteUrls.moonfire]: {
		RemoteURL: DemoRemoteUrls.moonfire,
		Commit: "HEAD",
		Branch: "main",
		BuildDirectory: "src",
		Arguments: "",
		Core: "cor_5a7849305bdb5be5a8f34e1aa45f8bc5",
	},
	[DemoRemoteUrls.taxFreeRetirement]: {
		RemoteURL: DemoRemoteUrls.taxFreeRetirement,
		Commit: "HEAD",
		Branch: "main",
		BuildDirectory: "src",
		Arguments: "",
		Core: "cor_9864a3b49f17503094e04b418af40ef0",
	},
	[DemoRemoteUrls.mlx]: {
		RemoteURL: DemoRemoteUrls.mlx,
		Commit: "HEAD",
		Branch: "main",
		BuildDirectory: "src",
		Arguments: "",
		Core: "cor_9864a3b49f17503094e04b418af40ef0",
	},
	[DemoRemoteUrls.bme]: {
		RemoteURL: DemoRemoteUrls.bme,
		Commit: "HEAD",
		Branch: "main",
		BuildDirectory: "src",
		Arguments: "",
		Core: "cor_e7eec4dce06f5a03a1b5418e0c5cbd7e",
	},
	[DemoRemoteUrls.brownHam]: {
		RemoteURL: DemoRemoteUrls.brownHam,
		Commit: "HEAD",
		Branch: "main",
		BuildDirectory: "src",
		Arguments: "",
		Core: "cor_6ba7d14f76635a85bfd757e084433afc",
	},
	[DemoRemoteUrls.var]: {
		RemoteURL: DemoRemoteUrls.var,
		Commit: "HEAD",
		Branch: "issue-26",
		BuildDirectory: "src",
		Arguments: "-j -S 3",
		Core: "cor_5a7849305bdb5be5a8f34e1aa45f8bc5",
	},
};

export const DemoCards: DemoApplicationCard[] = [
	{
		title: "Precipitate Dislocation Model from Brown and Ham",
		titleImage: `Oil-rig.svg`,
		description:
			"The precipitate dislocation model from Brown and Ham [1] provides metallurgists with insight into a metal alloy’s strength. This application implements the model but treats the inputs as probability distributions, and calculates the output cutting stress distribution using Signaloid’s uncertainty quantification technology.</br>" +
			`<cite>[1] Brown, L. M., and R. K. Ham. "Dislocation-particle interactions." Strengthening methods in crystals (1971): 9–135.</cite>`,
		chips: ["Materials Modeling", "C0Pro-XS"],
		link: "brown-and-ham",
		color: "gradients/g.blue",
		repoUrl: DemoRemoteUrls.brownHam,
		coreId: "cor_6ba7d14f76635a85bfd757e084433afc",
		authFreeBuildId: environment.BROWN_AND_HAM_AUTH_FREE_BUILD_ID,
	},
	{
		title: "Bosch BME680 Calibration Routines",
		titleImage: `Temperature-high.svg`,
		description:
			"Sensors use calibration algorithms to convert transducer readings into physical parameters. Signaloid’s technology lets you quantify how sensor noise and transducer measurement uncertainty can impact your system’s calibrated outputs.\n",
		chips: ["Sensor Manufacturing", "C0Pro-S"],
		link: "bme680",
		color: "gradients/g.orange",
		repoUrl: DemoRemoteUrls.bme,
		coreId: "cor_e7eec4dce06f5a03a1b5418e0c5cbd7e",
		authFreeBuildId: environment.BME680_AUTH_FREE_BUILD_ID,
	},
	{
		title: "Melexis MLX90640 Calibration Routines",
		titleImage: `Camera.svg`,
		description:
			"This example allows you to model the output uncertainty of the Melexis MLX90640 temperature sensor array, given an uncertainty of measurand emissivity, and taking into account the ADC quantization error of the sensor.",
		chips: ["Sensor Manufacturing", "C0Pro-S"],
		link: "mlx90640",
		color: "gradients/g.brand",
		repoUrl: DemoRemoteUrls.mlx,
		coreId: "cor_9864a3b49f17503094e04b418af40ef0",
		authFreeBuildId: environment.MLX_APP_AUTH_FREE_BUILD_ID,
	},
	{
		title: "Retirement Savings Modeler",
		titleImage: `Invoice.svg`,
		description:
			"This example estimates the final balance of a retirement savings account by modeling tax rates, interest rates, and other financial uncertainties as probability distributions.",
		chips: ["Financial", "C0Pro-Jupiter-S"],
		link: "tax-free-retirement",
		color: "gradients/g.blue",
		repoUrl: DemoRemoteUrls.taxFreeRetirement,
		coreId: "cor_9864a3b49f17503094e04b418af40ef0",
		authFreeBuildId: environment.IRA_AUTH_FREE_BUILD_ID,
	},
	{
		title: "Portfolio Modeling",
		titleImage: `Chart-candlestick.svg`,
		description:
			"In the ArXiv paper “Venture Capital Portfolio Construction” by Farina et al., they propose a model for the optimal size for a venture capital investment portfolio. This application implements this model, but replaces the Monte Carlo methods proposed in the original with Signaloid’s uncertainty-tracking technology.\n",
		chips: ["Financial", "C0Pro-M"],
		link: "portfolio-modeling",
		color: "gradients/g.blue",
		coreId: "cor_5a7849305bdb5be5a8f34e1aa45f8bc5",
		repoUrl: DemoRemoteUrls.moonfire,
		authFreeBuildId: environment.MOONFIRE_AUTH_FREE_BUILD_ID,
	},
	{
		title: "Battery State of Charge Estimation",
		titleImage: `Waste-electronic.svg`,
		description:
			"The state of charge of a battery expresses its remaining capacity as a percentage of its total capacity. Accurately estimating the state of charge is crucial in battery-powered systems. This example models the state of charge of a Panasonic CGR-17500 cell from a noisy voltage measurement.",
		chips: ["Energy Storage", "C0Pro-M"],
		link: "battery-state",
		color: "gradients/g.blue",
		repoUrl: DemoRemoteUrls.batteryState,
		coreId: "cor_7354fce65c415e639bd3bb60f48fd9a5",
		authFreeBuildId: environment.BATTERY_STATE_AUTH_FREE_BUILD_ID,
	},
	{
		title: "Value at Risk (VaR)",
		titleImage: `var.svg`,
		description:
			"Value at Risk (VaR) is a widely-used investment risk analysis tool for estimating potential financial losses over a specific time period. This application computes VaR deterministically in one calculation, and uses geometric Brownian motion to model the asset price over time, based on the implementation in “Mathematical Modeling And Computation In Finance” by Oosterlee and Grzelak.",
		chips: ["Materials Modeling", "C0Pro-XS"],
		link: "value-at-risk",
		color: "gradients/g.blue",
		repoUrl: DemoRemoteUrls.var,
		coreId: "cor_5a7849305bdb5be5a8f34e1aa45f8bc5",
		authFreeBuildId: environment.VAR_APP_AUTH_FREE_BUILD_ID,
	},
];

export const selectedApiDemoCards = DemoCards.slice(0, 3);
export const selectedDemosForExampleRepositories = DemoCards.slice(0, 3);
